import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { setLastPublicationNumber } from '../../../redux/slices/appSlice';
import {
  setPaginationPage,
  updatePaginationFromItems,
} from '../../../redux/slices/paginationSlice';
import {
  resetFiltersOnlyWithUpdate,
  setSearchValueWithUpdate,
  clearSelectedPostFilterWithUpdate,
} from '../../../redux/actions/filterActions';
import { setMobileMenuOpen } from '../../../redux/slices/mobileMenuSlice';
import styles from './index.module.scss';
import { renderPublication } from '../renderPublication';

// Импортируем функции из fncs.jsx
import {
  getPublicationsMatchingSearch,
  saveNavigationState,
  getNavigationState,
  navigateBack,
  navigateForward,
  handleBrowserNavigation,
  handlePageChange,
  handleShowAllPublications,
  handleResetSearch,
  getCurrentPublications,
  getStartIndex,
  hasSearchResults,
  getSearchResultsMessage,
} from './fncs';

const ListTemplatePage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { pageNumber } = useParams();

  // Redux selectors
  const publications = useSelector((state) => state.currentTab.publications);
  const searchValue = useSelector((state) => state.currentTab.search_value);
  const selectedPostFilter = useSelector((state) => state.currentTab.selected_post_filter);
  const filterAValue = useSelector((state) => state.currentTab.filter_a_value);
  const filterBValue = useSelector((state) => state.currentTab.filter_b_value);
  const originalPublications = useSelector((state) => state.currentTab.original_publications);
  const currentPaginationPage = useSelector((state) => state.pagination.currentPaginationPage);
  const totalPages = useSelector((state) => state.pagination.totalPages);
  const itemsPerPage = useSelector((state) => state.pagination.itemsPerPage);
  const lastPublicationNumber = useSelector((state) => state.app.lastPublicationNumber);
  const isBrowserNavigationFromRedux = useSelector((state) => state.app.isBrowserNavigation);
  const currentTab = useSelector((state) => state.currentTab.currentTab);
  const tabOrPublicationPage = useSelector((state) => state.app.tab_or_publication_page);

  // Счетчик рендеров для отладки
  const renderCount = React.useRef(0);
  renderCount.current += 1;

  // Отладочная информация
  // console.log('=== LIST TEMPLATE PAGE DEBUG ===');
  // console.log('publications:', publications);
  // console.log('publications length:', publications?.length);
  // console.log('currentTab:', currentTab);
  // console.log('location.pathname:', location.pathname);
  // console.log('tabOrPublicationPage:', tabOrPublicationPage);
  // console.log('================================');

  // Проверяем, соответствует ли текущий путь текущей вкладке
  const isCorrectPath = React.useMemo(() => {
    if (!currentTab || !currentTab.eng_name) return false;

    const pathArray = location.pathname.split('/').filter((item) => item !== '');
    
    if (currentTab.eng_name === 'main_tab') {
      // Для main_tab: '/' или '/:number'
      const isNumber = pathArray.length === 1 && /^\d+$/.test(pathArray[0]);
      return pathArray.length === 0 || isNumber;
    } else {
      // Для других разделов: '/section' или '/section/:number'
      const isNumber = pathArray.length === 2 && /^\d+$/.test(pathArray[1]);
      return (
        (pathArray.length === 1 && pathArray[0] === currentTab.eng_name) ||
        (pathArray.length === 2 && pathArray[0] === currentTab.eng_name && isNumber)
      );
    }
  }, [currentTab, location.pathname]);

  // console.log('isCorrectPath:', isCorrectPath);
  // console.log(
  //   'expectedPath:',
  //   currentTab?.eng_name === 'main_tab' ? '/' : `/${currentTab?.eng_name}`,
  // );

  // Local state
  const [paginationPath, setPaginationPath] = React.useState('');
  const [isFromNewsLine, setIsFromNewsLine] = React.useState(false);
  const [isBrowserNavigation, setIsBrowserNavigation] = React.useState(false);

  // Вычисляем количество публикаций, соответствующих поисковому запросу
  const publicationsMatchingSearch = React.useMemo(() => {
    const result = getPublicationsMatchingSearch(searchValue, originalPublications);
    // console.log('=== SEARCH DEBUG ===');
    // console.log('searchValue:', searchValue);
    // console.log('originalPublications:', originalPublications?.length);
    // console.log('publicationsMatchingSearch:', result?.length);
    // console.log('====================');
    return result;
  }, [searchValue, originalPublications]);

  // Вычисляем текущие публикации с учетом пагинации
  const currentPublications = React.useMemo(() => {
    return getCurrentPublications(publications, currentPaginationPage, itemsPerPage);
  }, [publications, currentPaginationPage, itemsPerPage]);

  // Вычисляем индекс начала страницы
  const startIndex = React.useMemo(() => {
    return getStartIndex(currentPaginationPage, itemsPerPage);
  }, [currentPaginationPage, itemsPerPage]);

  // Проверяем, есть ли результаты поиска
  const hasSearch = hasSearchResults(searchValue, publicationsMatchingSearch);
  
  // Проверяем, активен ли поиск (независимо от результатов)
  const isSearchActive = searchValue && searchValue.trim() !== '';

  // Получаем сообщение о результатах поиска
  const searchResultsMessage = getSearchResultsMessage(
    searchValue,
    publicationsMatchingSearch,
    publications?.length || 0,
  );

  // Получаем информацию о выбранном посте
  const selectedPost = React.useMemo(() => {
    if (!selectedPostFilter || !originalPublications) return null;
    return originalPublications.find((pub) => pub.eng_name === selectedPostFilter);
  }, [selectedPostFilter, originalPublications]);

  const hasSelectedPost = selectedPostFilter && selectedPost !== null;

  // Отладочная информация о рендерах и объектах
  // console.log('=== RENDER DEBUG ===');
  // console.log('Render count:', renderCount.current);
  // console.log('Current publications count:', currentPublications?.length || 0);
  // console.log('Publications matching search count:', publicationsMatchingSearch?.length || 0);
  // console.log('Total pages:', totalPages);
  // console.log('Current page:', currentPaginationPage);
  // console.log('Items per page:', itemsPerPage);
  // console.log('Start index:', startIndex);
  // console.log('========================');

  // Обработчики событий
  const handlePageChangeClick = (page) => {
    handlePageChange(page, dispatch, setPaginationPage, navigate, location, currentTab);
  };

  const handleShowAllPublicationsClick = () => {
    handleShowAllPublications(dispatch, resetFiltersOnlyWithUpdate);
  };

  const handleResetSearchClick = () => {
    handleResetSearch(dispatch, setSearchValueWithUpdate);
  };

  const handleClearSelectedPostClick = () => {
    dispatch(clearSelectedPostFilterWithUpdate());
  };

  // Обработчик навигации браузера
  const handlePopState = React.useCallback(
    handleBrowserNavigation(
      navigate,
      dispatch,
      setPaginationPage,
      setIsBrowserNavigation,
      setMobileMenuOpen,
      currentPaginationPage,
    ),
    [navigate, dispatch, currentPaginationPage],
  );

  // Эффекты
  React.useEffect(() => {
    // Синхронизируем номер страницы из URL с Redux
    const pathArray = location.pathname.split('/').filter((item) => item !== '');
    let pageFromUrl = 1;

    // Проверяем последний сегмент пути
    if (pathArray.length > 0) {
      const lastSegment = pathArray[pathArray.length - 1];
      const isNumber = /^\d+$/.test(lastSegment);
      
      // console.log('=== PAGINATION DEBUG ===');
      // console.log('pathname:', location.pathname);
      // console.log('pathArray:', pathArray);
      // console.log('lastSegment:', lastSegment);
      // console.log('isNumber:', isNumber);
      
      if (isNumber) {
        const pageNum = parseInt(lastSegment, 10);
        if (!isNaN(pageNum) && pageNum > 0) {
          pageFromUrl = pageNum;
        }
      }
      
      // console.log('pageFromUrl:', pageFromUrl);
      // console.log('currentPaginationPage:', currentPaginationPage);
      // console.log('========================');
    }

    // Обновляем Redux только если номер страницы изменился
    if (pageFromUrl !== currentPaginationPage) {
      dispatch(setPaginationPage(pageFromUrl));
    }
  }, [location.pathname, dispatch, currentPaginationPage]);

  React.useEffect(() => {
    // Добавляем слушатель навигации браузера
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handlePopState]);

  React.useEffect(() => {
    // Обновляем пагинацию при изменении публикаций
    if (publications && publications.length > 0) {
      dispatch(updatePaginationFromItems(publications.length));
    }
  }, [publications, dispatch]);

  // Сброс пагинации при изменении раздела (только если в URL нет номера страницы)
  React.useEffect(() => {
    if (currentTab?.eng_name) {
      const pathArray = location.pathname.split('/').filter((item) => item !== '');
      const lastSegment = pathArray.length > 0 ? pathArray[pathArray.length - 1] : '';
      const hasPageInUrl = /^\d+$/.test(lastSegment);
      
      // Сбрасываем только если в URL нет номера страницы
      if (!hasPageInUrl) {
        dispatch(setPaginationPage(1));
      }
    }
  }, [currentTab?.eng_name, dispatch, location.pathname]);

  // Сброс пагинации и URL при изменении фильтров
  const prevSearchValue = React.useRef(searchValue);
  const prevSelectedPostFilter = React.useRef(selectedPostFilter);
  const prevFilterAValue = React.useRef(filterAValue);
  const prevFilterBValue = React.useRef(filterBValue);

  React.useEffect(() => {
    // Проверяем, действительно ли изменились фильтры
    const filtersChanged = 
      prevSearchValue.current !== searchValue || 
      prevSelectedPostFilter.current !== selectedPostFilter ||
      prevFilterAValue.current !== filterAValue ||
      prevFilterBValue.current !== filterBValue;

    if (filtersChanged) {
      prevSearchValue.current = searchValue;
      prevSelectedPostFilter.current = selectedPostFilter;
      prevFilterAValue.current = filterAValue;
      prevFilterBValue.current = filterBValue;

      // Сбрасываем пагинацию
      dispatch(setPaginationPage(1));
      
      // Проверяем, есть ли номер страницы в URL
      const pathArray = location.pathname.split('/').filter((item) => item !== '');
      const lastSegment = pathArray.length > 0 ? pathArray[pathArray.length - 1] : '';
      const hasPageInUrl = /^\d+$/.test(lastSegment);

      // Если есть номер страницы в URL, убираем его
      if (hasPageInUrl) {
        let basePath;
        if (currentTab?.eng_name === 'main_tab') {
          basePath = '/';
        } else if (pathArray.length === 2) {
          basePath = `/${pathArray[0]}`;
        } else {
          basePath = location.pathname;
        }
        
        navigate(basePath, { replace: true });
      }
    }
  }, [searchValue, selectedPostFilter, filterAValue, filterBValue, dispatch, navigate, location.pathname, currentTab]);

  // Обработка возврата к публикации (отключено)
  React.useEffect(() => {
    if (
      false &&
      lastPublicationNumber &&
      publications &&
      publications.length > 0 &&
      (isBrowserNavigation || !isFromNewsLine)
    ) {
      const targetPage = Math.ceil(lastPublicationNumber / itemsPerPage);
      if (targetPage !== currentPaginationPage) {
        dispatch(setPaginationPage(targetPage));
      } else {
        setTimeout(() => {
          const publicationElement = document.querySelector(
            `[data-global-index="${lastPublicationNumber}"]`,
          );
          if (publicationElement) {
            const header = document.querySelector('header');
            const headerHeight = header ? header.offsetHeight : 60;
            const additionalOffset = 20;
            const totalOffset = headerHeight + additionalOffset;

            const rect = publicationElement.getBoundingClientRect();
            const absoluteY = rect.top + window.scrollY;
            window.scrollTo({
              top: absoluteY - totalOffset,
              behavior: isBrowserNavigation ? 'auto' : 'smooth',
            });
            dispatch(setLastPublicationNumber(null));
          } else {
            dispatch(setLastPublicationNumber(null));
          }
        }, 200);
      }
    }
  }, [
    lastPublicationNumber,
    publications,
    currentPaginationPage,
    itemsPerPage,
    dispatch,
    isBrowserNavigation,
    isFromNewsLine,
  ]);

  // Рендерим только когда путь соответствует текущей вкладке
  if (!isCorrectPath) {
    // console.log('=== RENDER BLOCKED ===');
    // console.log('Path mismatch - showing loading');
    // console.log('=======================');
    return (
      <div className={styles.listTemplatePage}>
        <div className={styles.loading}>
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  // Отладочная информация о том, что будет отрендерено
  // console.log('=== RENDER CONTENT DEBUG ===');
  // console.log('Will render publications:', currentPublications?.length || 0);
  // console.log('Will render search results:', hasSearch);
  // console.log('Will render pagination:', totalPages > 1);
  // console.log('============================');

  return (
    <div className={styles.listTemplatePage}>
      {/* Информация о выбранном посте */}
      {hasSelectedPost && (
        <div className={styles.searchResults}>
          <p className={styles.searchMessage}>
            Выбран пост: {selectedPost.show_name || selectedPost.title || selectedPost.eng_name}
          </p>
          <button
            className={styles.showAllButton}
            onClick={handleClearSelectedPostClick}
            title='Показать всё'>
            Показать всё
          </button>
        </div>
      )}

      {/* Результаты поиска и фильтрации */}
      {isSearchActive && !hasSelectedPost && (
        <div className={styles.searchResults}>
          <div className={styles.searchHeader}>
            <div className={styles.searchInfo}>
              <p className={styles.searchMessage}>
                Результаты поиска по запросу: <strong>"{searchValue}"</strong>
              </p>
              <p className={styles.searchCount}>
                Найдено: <strong>{publicationsMatchingSearch?.length || 0}</strong> {
                  (publicationsMatchingSearch?.length || 0) === 1 
                    ? 'результат' 
                    : (publicationsMatchingSearch?.length || 0) >= 2 && (publicationsMatchingSearch?.length || 0) <= 4
                      ? 'результата' 
                      : 'результатов'
                }
                {(publications?.length || 0) < (publicationsMatchingSearch?.length || 0) && (
                  <span className={styles.hiddenCount}>
                    {' '}(показано: {publications?.length || 0}, скрыто фильтрами: {(publicationsMatchingSearch?.length || 0) - (publications?.length || 0)})
                  </span>
                )}
              </p>
            </div>
          <button
              className={styles.resetSearchButton}
              onClick={handleResetSearchClick}
              title='Сбросить поиск'>
              Сбросить поиск
          </button>
          </div>
          {((filterAValue && filterAValue !== 'все') || (filterBValue && filterBValue !== 'все')) && 
            (publicationsMatchingSearch?.length || 0) > (publications?.length || 0) && (
              <p className={styles.filterInfo}>
                <strong>{(publicationsMatchingSearch?.length || 0) - (publications?.length || 0)}</strong> {
                  ((publicationsMatchingSearch?.length || 0) - (publications?.length || 0)) === 1 
                    ? 'результат не отображается' 
                    : ((publicationsMatchingSearch?.length || 0) - (publications?.length || 0)) >= 2 && 
                      ((publicationsMatchingSearch?.length || 0) - (publications?.length || 0)) <= 4
                      ? 'результата не отображаются' 
                      : 'результатов не отображаются'
                } из-за активных фильтров. Сбросьте фильтры для просмотра всех результатов поиска.
              </p>
            )
          }
        </div>
      )}

      {/* Список публикаций */}
      {publications && publications.length > 0 ? (
        <div className={styles.publicationsList}>
          {currentPublications.map((publication, index) => {
            const globalIndex = startIndex + index + 1;

            return (
              <div
                key={index}
                data-global-index={globalIndex}
                data-template-name={publication.template?.template_name}>
                {renderPublication(publication, index, globalIndex)}
              </div>
            );
          })}

          {/* Пагинация */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.paginationButton}
                onClick={() => handlePageChangeClick(currentPaginationPage - 1)}
                disabled={currentPaginationPage === 1}>
                Назад
              </button>

              <div className={styles.pageNumbers}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`${styles.pageButton} ${
                      currentPaginationPage === page ? styles.active : ''
                    }`}
                    onClick={() => handlePageChangeClick(page)}>
                    {page}
                  </button>
                ))}
              </div>

              <button
                className={styles.paginationButton}
                onClick={() => handlePageChangeClick(currentPaginationPage + 1)}
                disabled={currentPaginationPage === totalPages}>
                Вперед
              </button>
            </div>
          )}
        </div>
      ) : (
        <p>Публикации не найдены</p>
      )}
    </div>
  );
};

export default ListTemplatePage;
